// src/services/aiQuotaService.js
// ================================================================
//  سهمیه AI — Backend-Sync با json-server
//  سرور = منبع حقیقت | localStorage = کش
// ================================================================

export const FREE_DAILY_LIMIT = 3;

// ✅ تغییر یافت: اتصال به دروازه‌بان
const API_BASE = '/api/db';

const LS_QUOTA_KEY = (userId) => `ai_quota_cache_${userId}`;
const LS_SUB_KEY   = (userId) => `ai_sub_cache_${userId}`;

const SECRET = 'ai-quota-v2-a7f3e9c1b8d4';
const signPayload = (userId, payload) => {
  const str = `${SECRET}|${userId}|${JSON.stringify(payload)}`;
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(36);
};
const writeSigned = (key, userId, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify({ p: payload, s: signPayload(userId, payload) }));
  } catch {}
};
const readSigned = (key, userId) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.p || !obj?.s) return null;
    if (obj.s !== signPayload(userId, obj.p)) {
      localStorage.removeItem(key);
      return null;
    }
    return obj.p;
  } catch { return null; }
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const safeDate = (stored) => {
  const clientToday = todayKey();
  if (stored?.date && stored.date > clientToday) return stored.date;
  return clientToday;
};

const apiGet = async (path) => {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
};
const apiPost = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json();
};
const apiPut = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${path} → ${res.status}`);
  return res.json();
};

export const getQuota = async (userId) => {
  const clientToday = todayKey();
  const fallback = { date: clientToday, used: 0, limit: FREE_DAILY_LIMIT };
  if (!userId) return fallback;

  const cached = readSigned(LS_QUOTA_KEY(userId), userId);

  try {
    const arr = await apiGet(`/aiQuotas?userId=${encodeURIComponent(userId)}`);
    const serverRec = Array.isArray(arr) ? arr[0] : null;

    if (serverRec) {
      const realDate = safeDate(serverRec);
      if (realDate !== serverRec.date) {
        const reset = { ...serverRec, date: realDate, used: 0, updatedAt: new Date().toISOString() };
        await apiPut(`/aiQuotas/${serverRec.id}`, reset);
        writeSigned(LS_QUOTA_KEY(userId), userId, reset);
        return reset;
      }
      const merged = {
        ...serverRec,
        used: Math.max(Number(serverRec.used) || 0, Number(cached?.used) || 0),
        limit: serverRec.limit || FREE_DAILY_LIMIT,
      };
      if (merged.used > (serverRec.used || 0)) {
        await apiPut(`/aiQuotas/${serverRec.id}`, { ...merged, updatedAt: new Date().toISOString() });
      }
      writeSigned(LS_QUOTA_KEY(userId), userId, merged);
      return merged;
    }

    const fresh = cached && cached.date === clientToday
      ? { ...cached, limit: cached.limit || FREE_DAILY_LIMIT }
      : fallback;

    const created = await apiPost('/aiQuotas', {
      userId, date: fresh.date, used: fresh.used, limit: fresh.limit,
      updatedAt: new Date().toISOString(),
    });
    writeSigned(LS_QUOTA_KEY(userId), userId, created);
    return created;
  } catch (e) {
    console.warn('⚠️ getQuota: server unreachable', e.message);
    if (cached && cached.date === clientToday) return cached;
    return fallback;
  }
};

export const consumeQuota = async (userId) => {
  if (!userId) return { ok: false, reason: 'no-user' };

  const sub = await getSubscription(userId);
  if (sub) return { ok: true, subscription: true, quota: await getQuota(userId) };

  const current = await getQuota(userId);
  if (current.used >= current.limit) {
    return { ok: false, reason: 'quota-exceeded', quota: current };
  }

  const updated = { ...current, used: current.used + 1, updatedAt: new Date().toISOString() };
  writeSigned(LS_QUOTA_KEY(userId), userId, updated);

  try {
    if (updated.id) {
      await apiPut(`/aiQuotas/${updated.id}`, updated);
    } else {
      const created = await apiPost('/aiQuotas', { ...updated, userId });
      writeSigned(LS_QUOTA_KEY(userId), userId, created);
      return { ok: true, quota: created };
    }
  } catch (e) {
    console.warn('⚠️ consumeQuota sync failed', e.message);
  }
  return { ok: true, quota: updated };
};

export const refundQuota = async (userId) => {
  if (!userId) return;
  const sub = await getSubscription(userId);
  if (sub) return;

  const current = await getQuota(userId);
  if (!current.used || current.used <= 0) return;

  const updated = { ...current, used: Math.max(0, current.used - 1), updatedAt: new Date().toISOString() };
  writeSigned(LS_QUOTA_KEY(userId), userId, updated);

  try {
    if (updated.id) await apiPut(`/aiQuotas/${updated.id}`, updated);
  } catch (e) {
    console.warn('⚠️ refundQuota sync failed', e.message);
  }
};

export const getSubscription = async (userId) => {
  if (!userId) return null;
  const cached = readSigned(LS_SUB_KEY(userId), userId);

  try {
    const arr = await apiGet(`/aiSubscriptions?userId=${encodeURIComponent(userId)}`);
    const serverSub = Array.isArray(arr) ? arr[0] : null;
    if (serverSub) {
      if (!serverSub.expiresAt || new Date(serverSub.expiresAt).getTime() <= Date.now()) {
        try { await fetch(`${API_BASE}/aiSubscriptions/${serverSub.id}`, { method: 'DELETE' }); } catch {}
        localStorage.removeItem(LS_SUB_KEY(userId));
        return null;
      }
      writeSigned(LS_SUB_KEY(userId), userId, serverSub);
      return serverSub;
    }
  } catch (e) {
    console.warn('⚠️ getSubscription: server unreachable', e.message);
  }

  if (cached && cached.expiresAt && new Date(cached.expiresAt).getTime() > Date.now()) {
    return cached;
  }
  return null;
};

export const canGenerate = async (userId) => {
  const sub = await getSubscription(userId);
  if (sub) return { allowed: true, reason: 'subscribed', subscription: sub, remaining: Infinity };
  const quota = await getQuota(userId);
  const remaining = Math.max(0, quota.limit - quota.used);
  if (remaining > 0) return { allowed: true, reason: 'free', quota, remaining };
  return { allowed: false, reason: 'quota-exceeded', quota, remaining: 0 };
};

export const activateSubscription = async (userId, { plan, months, paymentId } = {}) => {
  if (!userId) return null;
  const m = months || (plan === 'yearly' ? 12 : 1);
  const now = new Date();
  const expires = new Date(now);
  expires.setMonth(expires.getMonth() + m);

  const payload = {
    userId,
    plan: plan || 'monthly',
    months: m,
    activatedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
    paymentId: paymentId || null,
    updatedAt: now.toISOString(),
  };

  writeSigned(LS_SUB_KEY(userId), userId, payload);

  try {
    const arr = await apiGet(`/aiSubscriptions?userId=${encodeURIComponent(userId)}`);
    const existing = Array.isArray(arr) ? arr[0] : null;
    if (existing) {
      const merged = await apiPut(`/aiSubscriptions/${existing.id}`, { ...existing, ...payload });
      writeSigned(LS_SUB_KEY(userId), userId, merged);
      return merged;
    }
    const created = await apiPost('/aiSubscriptions', payload);
    writeSigned(LS_SUB_KEY(userId), userId, created);
    return created;
  } catch (e) {
    console.warn('⚠️ activateSubscription: server sync failed', e.message);
    return payload;
  }
};

export const AI_PLANS = Object.freeze({
  monthly: {
    id: 'monthly', title: 'اشتراک ماهانه', months: 1,
    price: 99000, priceLabel: '۹۹٬۰۰۰ تومان',
    features: ['ساخت نامحدود آزمون با AI', 'بدون محدودیت روزانه', 'دسترسی کامل به همه مدل‌ها'],
  },
  yearly: {
    id: 'yearly', title: 'اشتراک سالانه', months: 12,
    price: 790000, priceLabel: '۷۹۰٬۰۰۰ تومان', badge: '۲ ماه هدیه 🎁',
    features: ['ساخت نامحدود آزمون با AI', 'بدون محدودیت روزانه', 'دسترسی کامل به همه مدل‌ها', '۲ ماه هدیه'],
  },
});

export const formatExpiry = (iso) => {
  try { return new Date(iso).toLocaleDateString('fa-IR'); }
  catch { return '—'; }
};